/**
 * Contract with ischool-api's two-phase exchange endpoint
 * (POST /api/v1/public/livekit/connection_details — plan v4 §2.5).
 *
 * phase "prejoin": validates the code WITHOUT consuming it; returns session
 * context for the device-pick screen. Repeatable.
 * phase "connect": consumes the code (single use), creates the LiveKit room
 * (auto-egress starts here), mints the access token, and returns a reconnect
 * token this client keeps in sessionStorage for refresh/reconnect.
 */

export type AppRole = 'Tutor' | 'Student' | 'Admin' | 'Manager' | 'QualityMember' | 'Parent'

export interface SessionContext {
  id: number
  type: '1:1' | 'group'
  start_at: string
  end_at: string
}

/** Server-assigned, from the token — never something the client decides. */
/**
 * The meeting role the API mints into the LiveKit token.
 * Meetings::ConnectionDetails#meeting_role_for returns exactly these three:
 * Host (tutor), Participant (student or parent), Ghost (hidden observer). There
 * is no Moderator role on the wire, so it is not modelled here — gating on it
 * would be dead code.
 */
export type MeetingRole = 'Host' | 'Participant' | 'Ghost'

export interface CurrentUserContext {
  name: string
  avatar_url: string | null
  appRole: AppRole
  meetingRole: MeetingRole
}

export interface PrejoinData {
  session: SessionContext
  current_user: CurrentUserContext
}

export interface ConnectData extends PrejoinData {
  server_url: string
  token: string
  room_name: string
  identity: string
  reconnect_token: string
  /**
   * A replacement join code. The one just used stays valid until this one is
   * used, at which point the server retires it — so a link copied out of the
   * address bar earlier stops working while the real participant keeps a
   * working one. Absent for single-use observer links.
   */
  next_code: string | null
}

/** Rails Responseable envelope: every response is { status, message, data }. */
export interface ApiEnvelope<T> {
  status: 'success' | 'failure'
  message: string
  data: T
}

export type JoinErrorKind =
  | 'expired' // code/reconnect no longer valid (consumed, expired, or stale generation)
  | 'not_started' // outside the join window, too early
  | 'ended' // session is over
  | 'switched' // session moved back to Zoom (provider switch) — reopen from the app
  | 'unavailable' // server is shedding load (503/429) — wait, do not hammer
  | 'waiting_for_class' // 425: a Ghost reached the room before a teacher opened it — auto-retry
  | 'network'
  | 'unknown'

/**
 * Whether the user can usefully retry in place. Reloading is NEVER safe advice:
 * the one-time code lives in a fragment that boot already scrubbed, so a reload
 * loses the credential entirely — retry has to happen without navigating.
 */
export function isRetryable(kind: JoinErrorKind): boolean {
  return kind === 'network' || kind === 'unknown' || kind === 'unavailable'
}

/**
 * How long the retry button stays disabled. `unavailable` means the server is in
 * a cool-off (circuit breaker, ~30s): letting the user re-tap immediately just
 * burns the per-IP throttle, which on a shared mobile network punishes every
 * other student behind the same address.
 */
export function retryDelaySeconds(kind: JoinErrorKind): number {
  return kind === 'unavailable' ? 30 : 0
}
